import React, { lazy, Suspense } from "react";
import { graphql } from "gatsby";

import Seo from "@/components/seo.jsx";
import NotificationToaster from "@/components/notification-toaster";
import Layout from "@/components/layout";
import { transformKeywords } from "@/components/replaceKeywords";

const Templates = {
  StrapiComponentCustomOffWhiteGrid: lazy(
    () => import("@/components/blocks/custom/off-white-grid")
  ),
  StrapiComponentCustomOffsetWithSupportingText: lazy(
    () => import("@/components/blocks/custom/offset-with-supporting-text")
  ),

  StrapiComponentArticleCentered: lazy(
    () => import("@/components/blocks/article/centered")
  ),

  StrapiComponentBlogThreeColumnCardsWithFilter: lazy(
    () => import("@/components/blocks/blog/three-column-cards-with-filter")
  ),
  StrapiComponentBlogThreeColumnCardsWithPagination: lazy(
    () => import("@/components/blocks/blog/three-column-cards-with-pagination")
  ),
  StrapiComponentBlogThreeColumnCards: lazy(
    () => import("@/components/blocks/blog/three-column-cards")
  ),
  StrapiComponentBlogThreeColumnWithBadges: lazy(
    () => import("@/components/blocks/blog/three-column-with-badges")
  ),
  StrapiComponentBlogTwoColumnWithSignUp: lazy(
    () => import("@/components/blocks/blog/two-column-with-sign-up")
  ),

  StrapiComponentContactCentered: lazy(
    () => import("@/components/blocks/contact/centered")
  ),
  StrapiComponentContactSideBySideGrid: lazy(
    () => import("@/components/blocks/contact/side-by-side-grid")
  ),

  StrapiComponentContentCentered: lazy(
    () => import("@/components/blocks/content/centered")
  ),
  StrapiComponentContentSimpleMedia: lazy(
    () => import("@/components/blocks/content/simple-media")
  ),
  StrapiComponentContentSplitWithImage: lazy(
    () => import("@/components/blocks/content/split-with-image")
  ),
  StrapiComponentContentTwoColumnsWithImage: lazy(
    () => import("@/components/blocks/content/two-columns-with-image")
  ),
  StrapiComponentContentTwoColumnsWithTestimonial: lazy(
    () => import("@/components/blocks/content/two-columns-with-testimonial")
  ),
  StrapiComponentContentTwoColumns: lazy(
    () => import("@/components/blocks/content/two-columns")
  ),
  StrapiComponentContentWithTestimonialAndStats: lazy(
    () => import("@/components/blocks/content/with-testimonial-and-stats")
  ),

  StrapiComponentCtaBrandPanelWithAppScreenshot: lazy(
    () => import("@/components/blocks/cta/brand-panel-with-app-screenshot")
  ),
  StrapiComponentCtaBrandPanelWithOverlappingImage: lazy(
    () => import("@/components/blocks/cta/brand-panel-with-overlapping-image")
  ),
  StrapiComponentCtaSimpleCenterBranded: lazy(
    () => import("@/components/blocks/cta/simple-center-branded")
  ),
  StrapiComponentCtaSimpleCenterWithImageBackground: lazy(
    () => import("@/components/blocks/cta/simple-center-with-image-background")
  ),
  StrapiComponentCtaSimpleCentered: lazy(
    () => import("@/components/blocks/cta/simple-centered")
  ),
  StrapiComponentCtaSimpleJustifiedOnLightBrand: lazy(
    () => import("@/components/blocks/cta/simple-justified-on-light-brand")
  ),
  StrapiComponentCtaSimpleJustified: lazy(
    () => import("@/components/blocks/cta/simple-justified")
  ),
  StrapiComponentCtaSimpleStacked: lazy(
    () => import("@/components/blocks/cta/simple-stacked")
  ),
  StrapiComponentCtaSplitWithImage: lazy(
    () => import("@/components/blocks/cta/split-with-image")
  ),

  StrapiComponentFaqCategoryHubFaq: lazy(
    () => import("@/components/blocks/faq/category-hub-faq")
  ),
  StrapiComponentFaqCenteredAccordion: lazy(
    () => import("@/components/blocks/faq/centered-accordion")
  ),
  StrapiComponentFaqOffsetWithSupportingText: lazy(
    () => import("@/components/blocks/faq/offset-with-supporting-text")
  ),
  StrapiComponentFaqSideBySide: lazy(
    () => import("@/components/blocks/faq/side-by-side")
  ),
  StrapiComponentFaqThreeColumns: lazy(
    () => import("@/components/blocks/faq/three-columns")
  ),
  StrapiComponentFaqTwoColumnsOnBrand: lazy(
    () => import("@/components/blocks/faq/two-columns-on-brand")
  ),
  StrapiComponentFaqTwoColumnsWithDescriptionOnDark: lazy(
    () => import("@/components/blocks/faq/two-columns-with-description-on-dark")
  ),
  StrapiComponentFaqTwoColumnsWithDescription: lazy(
    () => import("@/components/blocks/faq/two-columns-with-description")
  ),
  StrapiComponentFaqTwoColumns: lazy(
    () => import("@/components/blocks/faq/two-columns")
  ),

  StrapiComponentFeaturesCentered2X2Grid: lazy(
    () => import("@/components/blocks/features/centered-2x2-grid")
  ),
  StrapiComponentFeaturesGrid4X2OnBrand: lazy(
    () => import("@/components/blocks/features/grid-4x2-on-brand")
  ),
  StrapiComponentFeaturesGridWithOffsetIconsWithBigIcons: lazy(
    () =>
      import(
        "@/components/blocks/features/grid-with-offset-icons-with-big-icons"
      )
  ),
  StrapiComponentFeaturesGridWithOffsetIcons: lazy(
    () => import("@/components/blocks/features/grid-with-offset-icons")
  ),
  StrapiComponentFeaturesOffset2X2Grid: lazy(
    () => import("@/components/blocks/features/offset-2x2-grid")
  ),
  StrapiComponentFeaturesSimpleHeader: lazy(
    () => import("@/components/blocks/features/simple-header")
  ),
  StrapiComponentFeaturesSimpleThreeColumn: lazy(
    () => import("@/components/blocks/features/simple-three-column")
  ),
  StrapiComponentFeaturesWithFeatureGridList: lazy(
    () => import("@/components/blocks/features/with-feature-grid-list")
  ),
  StrapiComponentFeaturesWithFeatureList: lazy(
    () => import("@/components/blocks/features/with-feature-list")
  ),
  StrapiComponentFeaturesWithLargeScreenshot: lazy(
    () => import("@/components/blocks/features/with-large-screenshot")
  ),
  StrapiComponentFeaturesWithVariableImage: lazy(
    () => import("@/components/blocks/features/with-variable-image")
  ),

  StrapiComponentFeedSimpleWithDescriptionAlternating: lazy(
    () => import("@/components/blocks/feed/simple-with-description-alternating")
  ),
  StrapiComponentFeedSimpleWithDescription: lazy(
    () => import("@/components/blocks/feed/simple-with-description")
  ),

  StrapiComponentHeaderBrandedWithBackgroundImage: lazy(
    () => import("@/components/blocks/header/branded-with-background-image")
  ),
  StrapiComponentHeaderSimpleCentered: lazy(
    () => import("@/components/blocks/header/simple-centered")
  ),
  StrapiComponentHeaderSimpleWithSelectMenuDark: lazy(
    () => import("@/components/blocks/header/simple-with-select-menu-dark")
  ),
  StrapiComponentHeaderSimpleWithSelectMenu: lazy(
    () => import("@/components/blocks/header/simple-with-select-menu")
  ),
  StrapiComponentHeaderWithBackgroundImageAndOverlappingCards: lazy(
    () =>
      import(
        "@/components/blocks/header/with-background-image-and-overlapping-cards"
      )
  ),

  StrapiComponentHeroCardWithBackgroundImage: lazy(
    () => import("@/components/blocks/hero/card-with-background-image")
  ),
  StrapiComponentHeroDarkWithIllustration: lazy(
    () => import("@/components/blocks/hero/dark-with-illustration")
  ),
  StrapiComponentHeroImageCarousel: lazy(
    () => import("@/components/blocks/hero/image-carousel")
  ),
  StrapiComponentHeroSimpleCentered: lazy(
    () => import("@/components/blocks/hero/simple-centered")
  ),
  StrapiComponentHeroSplitWithNavbar: lazy(
    () => import("@/components/blocks/hero/split-with-navbar")
  ),
  StrapiComponentHeroSplitWithScreenshot: lazy(
    () => import("@/components/blocks/hero/split-with-screenshot")
  ),
  StrapiComponentHeroStaticBackgroundWithLogos: lazy(
    () => import("@/components/blocks/hero/static-background-with-logos")
  ),
  StrapiComponentHeroWithAngledImageOnRight: lazy(
    () => import("@/components/blocks/hero/with-angled-image-on-right")
  ),
  StrapiComponentHeroWithAppScreenshot: lazy(
    () => import("@/components/blocks/hero/with-app-screenshot")
  ),
  StrapiComponentHeroWithSignInForm: lazy(
    () => import("@/components/blocks/hero/with-sign-in-form")
  ),
  StrapiComponentHeroWithSignUpAndMediaContent: lazy(
    () => import("@/components/blocks/hero/with-sign-up-and-media-content")
  ),

  StrapiComponentLogoCloudOffWhiteGrid: lazy(
    () => import("@/components/blocks/logo-cloud/off-white-grid")
  ),
  StrapiComponentLogoCloudSimpleWithHeadingOnBrand: lazy(
    () => import("@/components/blocks/logo-cloud/simple-with-heading-on-brand")
  ),
  StrapiComponentLogoCloudSimpleWithHeading: lazy(
    () => import("@/components/blocks/logo-cloud/simple-with-heading")
  ),
  StrapiComponentLogoCloudSimple: lazy(
    () => import("@/components/blocks/logo-cloud/simple")
  ),
  StrapiComponentLogoCloudSplitWithGridOnRight: lazy(
    () => import("@/components/blocks/logo-cloud/split-with-grid-on-right")
  ),

  StrapiComponentNewsletterCenteredCardWithGraphic: lazy(
    () => import("@/components/blocks/newsletter/centered-card-with-graphic")
  ),
  StrapiComponentNewsletterSimpleStacked: lazy(
    () => import("@/components/blocks/newsletter/simple-stacked")
  ),
  StrapiComponentNewsletterSimpleWithDescription: lazy(
    () => import("@/components/blocks/newsletter/simple-with-description")
  ),
  StrapiComponentNewsletterWithDescriptionOnBrandCard: lazy(
    () =>
      import("@/components/blocks/newsletter/with-description-on-brand-card")
  ),
  StrapiComponentNewsletterWithParagraphOnBrandCard: lazy(
    () => import("@/components/blocks/newsletter/with-paragraph-on-brand-card")
  ),
  StrapiComponentNewsletterWithParagraphOnDark: lazy(
    () => import("@/components/blocks/newsletter/with-paragraph-on-dark")
  ),

  StrapiComponentPricingComparisonTable: lazy(
    () => import("@/components/blocks/pricing/comparison-table")
  ),

  StrapiComponentSharedPaddingComponent: lazy(
    () => import("@/components/blocks/shared/padding-component")
  ),

  StrapiComponentShopProductDetailWithImageGrid: lazy(
    () => import("@/components/blocks/shop-product-detail/with-image-grid")
  ),

  StrapiComponentShopProductListSimpleProductGrid: lazy(
    () => import("@/components/blocks/shop-product-list/simple-product-grid")
  ),
  StrapiComponentShopProductListWithInlineActionsAndExpandableSidebarFilters:
    lazy(
      () =>
        import(
          "@/components/blocks/shop-product-list/with-inline-actions-and-expandable-sidebar-filters"
        )
    ),

  StrapiComponentStatsSimpleInCard: lazy(
    () => import("@/components/blocks/stats/simple-in-card")
  ),
  StrapiComponentStatsSimpleOnBrand: lazy(
    () => import("@/components/blocks/stats/simple-on-brand")
  ),
  StrapiComponentStatsSplitWithImage: lazy(
    () => import("@/components/blocks/stats/split-with-image")
  ),
  StrapiComponentStatsWithFadingBackgroundImage: lazy(
    () => import("@/components/blocks/stats/with-fading-background-image")
  ),

  StrapiComponentTeamDarkVersionWithLargeImages: lazy(
    () => import("@/components/blocks/team/dark-version-with-large-images")
  ),
  StrapiComponentTeamFullWidthWithVerticalImages: lazy(
    () => import("@/components/blocks/team/full-width-with-vertical-images")
  ),
  StrapiComponentTeamGridWithLargeRoundImages: lazy(
    () => import("@/components/blocks/team/grid-with-large-round-images")
  ),
  StrapiComponentTeamGridWithRoundImages: lazy(
    () => import("@/components/blocks/team/grid-with-round-images")
  ),
  StrapiComponentTeamWithImageAndShortParagraph: lazy(
    () => import("@/components/blocks/team/with-image-and-short-paragraph")
  ),
  StrapiComponentTeamWithLargeImages: lazy(
    () => import("@/components/blocks/team/with-large-images")
  ),
  StrapiComponentTeamWithSmallImages: lazy(
    () => import("@/components/blocks/team/with-small-images")
  ),
  StrapiComponentTeamWithVerticalImages: lazy(
    () => import("@/components/blocks/team/with-vertical-images")
  ),

  StrapiComponentTestimonialSideBySideOnBrand: lazy(
    () => import("@/components/blocks/testimonial/side-by-side-on-brand")
  ),
  StrapiComponentTestimonialSimpleCentered: lazy(
    () => import("@/components/blocks/testimonial/simple-centered")
  ),
  StrapiComponentTestimonialWithBackgroundImage: lazy(
    () => import("@/components/blocks/testimonial/with-background-image")
  ),
  StrapiComponentTestimonialWithLargeAvatar: lazy(
    () => import("@/components/blocks/testimonial/with-large-avatar")
  ),
  StrapiComponentTestimonialWithOverlappingImage: lazy(
    () => import("@/components/blocks/testimonial/with-overlapping-image")
  ),
};

export const query = graphql`
  query pageQuery(
    $locale: String
    $pageId: String
    $articleIds: [String]
    $authorIds: [String]
    $categoryIds: [String]
    $productIds: [String]
  ) {
    page: strapiPage(id: { eq: $pageId }) {
      id
      strapiId
      url
      title
      isTemplateFor
      components {
        __typename

        ...CustomOffWhiteGridData
        ...CustomOffsetWithSupportingTextData

        ...ArticleCenteredData

        ...BlogThreeColumnCardsWithFilterData
        ...BlogThreeColumnCardsWithPaginationData
        ...BlogThreeColumnCardsData
        ...BlogThreeColumnWithBadgesData
        ...BlogTwoColumnWithSignUpData

        ...ContactCenteredData
        ...ContactSideBySideGridData

        ...ContentCenteredData
        ...ContentSimpleMediaData
        ...ContentSplitWithImageData
        ...ContentTwoColumnsWithImageData
        ...ContentTwoColumnsWithTestimonialData
        ...ContentTwoColumnsData
        ...ContentWithTestimonialAndStatsData

        ...CtaBrandPanelWithAppScreenshotData
        ...CtaBrandPanelWithOverlappingImageData
        ...CtaSimpleCenterBrandedData
        ...CtaSimpleCenterWithImageBackgroundData
        ...CtaSimpleCenteredData
        ...CtaSimpleJustifiedOnLightBrandData
        ...CtaSimpleJustifiedData
        ...CtaSimpleStackedData
        ...CtaSplitWithImageData

        ...FaqCategoryHubFaqData
        ...FaqCenteredAccordionData
        ...FaqOffsetWithSupportingTextData
        ...FaqSideBySideData
        ...FaqThreeColumnsData
        ...FaqTwoColumnsOnBrandData
        ...FaqTwoColumnsWithDescriptionOnDarkData
        ...FaqTwoColumnsWithDescriptionData
        ...FaqTwoColumnsData

        ...FeaturesCentered2x2GridData
        ...FeaturesGrid4x2OnBrandData
        ...FeaturesGridWithOffsetIconsWithBigIconsData
        ...FeaturesGridWithOffsetIconsData
        ...FeaturesOffset2x2GridData
        ...FeaturesSimpleHeaderData
        ...FeaturesSimpleThreeColumnData
        ...FeaturesWithFeatureGridListData
        ...FeaturesWithFeatureListData
        ...FeaturesWithLargeScreenshotData
        ...FeaturesWithVariableImageData

        ...FeedSimpleWithDescriptionAlternatingData
        ...FeedSimpleWithDescriptionData

        ...HeaderBrandedWithBackgroundImageData
        ...HeaderSimpleCenteredData
        ...HeaderSimpleWithSelectMenuDarkData
        ...HeaderSimpleWithSelectMenuData
        ...HeaderWithBackgroundImageAndOverlappingCardsData

        ...HeroCardWithBackgroundImageData
        ...HeroDarkWithIllustrationData
        ...HeroImageCarouselData
        ...HeroSimpleCenteredData
        ...HeroSplitWithNavbarData
        ...HeroSplitWithScreenshotData
        ...HeroStaticBackgroundWithLogosData
        ...HeroWithAngledImageOnRightData
        ...HeroWithAppScreenshotData
        ...HeroWithSignInFormData
        ...HeroWithSignUpAndMediaContentData

        ...LogoCloudOffWhiteGridData
        ...LogoCloudSimpleWithHeadingOnBrandData
        ...LogoCloudSimpleWithHeadingData
        ...LogoCloudSimpleData
        ...LogoCloudSplitWithGridOnRightData

        ...NewsletterCenteredCardWithGraphicData
        ...NewsletterSimpleStackedData
        ...NewsletterSimpleWithDescriptionData
        ...NewsletterWithDescriptionOnBrandCardData
        ...NewsletterWithParagraphOnBrandCardData
        ...NewsletterWithParagraphOnDarkData

        ...PricingComparisonTableData

        ...SharedPaddingComponentData

        ...ShopProductDetailWithImageGridData

        ...ShopProductListSimpleProductGridData

        ...StatsSimpleInCardData
        ...StatsSimpleOnBrandData
        ...StatsSplitWithImageData
        ...StatsWithFadingBackgroundImageData

        ...TeamDarkVersionWithLargeImagesData
        ...TeamFullWidthWithVerticalImagesData
        ...TeamGridWithLargeRoundImagesData
        ...TeamGridWithRoundImagesData
        ...TeamWithImageAndShortParagraphData
        ...TeamWithLargeImagesData
        ...TeamWithSmallImagesData
        ...TeamWithVerticalImagesData

        ...TestimonialSideBySideOnBrandData
        ...TestimonialSimpleCenteredData
        ...TestimonialWithBackgroundImageData
        ...TestimonialWithLargeAvatarData
        ...TestimonialWithOverlappingImageData

        ... on StrapiComponentSharedSharedComponent {
          shared_component {
            components {
              __typename
              ...CustomOffWhiteGridData
              ...CustomOffsetWithSupportingTextData

              ...ArticleCenteredData

              ...BlogThreeColumnCardsWithFilterData
              ...BlogThreeColumnCardsWithPaginationData
              ...BlogThreeColumnCardsData
              ...BlogThreeColumnWithBadgesData
              ...BlogTwoColumnWithSignUpData

              ...ContactCenteredData
              ...ContactSideBySideGridData

              ...ContentCenteredData
              ...ContentSimpleMediaData
              ...ContentSplitWithImageData
              ...ContentTwoColumnsWithImageData
              ...ContentTwoColumnsWithTestimonialData
              ...ContentTwoColumnsData
              ...ContentWithTestimonialAndStatsData

              ...CtaBrandPanelWithAppScreenshotData
              ...CtaBrandPanelWithOverlappingImageData
              ...CtaSimpleCenterBrandedData
              ...CtaSimpleCenterWithImageBackgroundData
              ...CtaSimpleCenteredData
              ...CtaSimpleJustifiedOnLightBrandData
              ...CtaSimpleJustifiedData
              ...CtaSimpleStackedData
              ...CtaSplitWithImageData

              ...FaqCategoryHubFaqData
              ...FaqCenteredAccordionData
              ...FaqOffsetWithSupportingTextData
              ...FaqSideBySideData
              ...FaqThreeColumnsData
              ...FaqTwoColumnsOnBrandData
              ...FaqTwoColumnsWithDescriptionOnDarkData
              ...FaqTwoColumnsWithDescriptionData
              ...FaqTwoColumnsData

              ...FeaturesCentered2x2GridData
              ...FeaturesGrid4x2OnBrandData
              ...FeaturesGridWithOffsetIconsWithBigIconsData
              ...FeaturesGridWithOffsetIconsData
              ...FeaturesOffset2x2GridData
              ...FeaturesSimpleHeaderData
              ...FeaturesSimpleThreeColumnData
              ...FeaturesWithFeatureGridListData
              ...FeaturesWithFeatureListData
              ...FeaturesWithLargeScreenshotData
              ...FeaturesWithVariableImageData

              ...FeedSimpleWithDescriptionAlternatingData
              ...FeedSimpleWithDescriptionData

              ...HeaderBrandedWithBackgroundImageData
              ...HeaderSimpleCenteredData
              ...HeaderSimpleWithSelectMenuDarkData
              ...HeaderSimpleWithSelectMenuData
              ...HeaderWithBackgroundImageAndOverlappingCardsData

              ...HeroCardWithBackgroundImageData
              ...HeroDarkWithIllustrationData
              ...HeroImageCarouselData
              ...HeroSimpleCenteredData
              ...HeroSplitWithNavbarData
              ...HeroSplitWithScreenshotData
              ...HeroStaticBackgroundWithLogosData
              ...HeroWithAngledImageOnRightData
              ...HeroWithAppScreenshotData
              ...HeroWithSignInFormData
              ...HeroWithSignUpAndMediaContentData

              ...LogoCloudOffWhiteGridData
              ...LogoCloudSimpleWithHeadingOnBrandData
              ...LogoCloudSimpleWithHeadingData
              ...LogoCloudSimpleData
              ...LogoCloudSplitWithGridOnRightData

              ...NewsletterCenteredCardWithGraphicData
              ...NewsletterSimpleStackedData
              ...NewsletterSimpleWithDescriptionData
              ...NewsletterWithDescriptionOnBrandCardData
              ...NewsletterWithParagraphOnBrandCardData
              ...NewsletterWithParagraphOnDarkData

              ...PricingComparisonTableData

              ...SharedPaddingComponentData

              ...ShopProductDetailWithImageGridData

              ...ShopProductListSimpleProductGridData

              ...StatsSimpleInCardData
              ...StatsSimpleOnBrandData
              ...StatsSplitWithImageData
              ...StatsWithFadingBackgroundImageData

              ...TeamDarkVersionWithLargeImagesData
              ...TeamFullWidthWithVerticalImagesData
              ...TeamGridWithLargeRoundImagesData
              ...TeamGridWithRoundImagesData
              ...TeamWithImageAndShortParagraphData
              ...TeamWithLargeImagesData
              ...TeamWithSmallImagesData
              ...TeamWithVerticalImagesData

              ...TestimonialSideBySideOnBrandData
              ...TestimonialSimpleCenteredData
              ...TestimonialWithBackgroundImageData
              ...TestimonialWithLargeAvatarData
              ...TestimonialWithOverlappingImageData
            }
          }
        }
      }
    }
    layout: strapiLayout(locale: { eq: $locale }) {
      locale
      noHeroPages {
        id
      }
      components {
        __typename
        ...FooterColumnsWithLeadingCompanyLogoData
        ...FooterFourColumnWithAddressData
        ...FooterFourColumnWithCompanyMissionData
        ...FooterFourColumnWithNewsletterAndLocalizationDarkData
        ...FooterFourColumnWithNewsletterAndLocalizationDarkData
        ...FooterFourColumnWithNewsletterDarkData
        ...FooterFourColumnWithNewsletterData
        ...FooterSimpleCenteredData
        ...FooterSocialLinksOnlyData

        ...NavbarCenteredLogoWithCartPopupData
        ...NavbarCenteredWithBottomBorderData
        ...NavbarSimpleBrandedWithStackedLinksData
        ...NavbarSimpleLinksCenteredData
        ...NavbarSimpleLinksOnLeftData
        ...NavbarWithFullWidthFlyoutsData

        ...BlogThreeColumnCardsData
        ...HeroSplitWithNavbarData
        ...HeroStaticBackgroundWithLogosData
      }
    }
    articles: allStrapiArticle(
      filter: { id: { in: $articleIds } }
      sort: { date: DESC }
    ) {
      nodes {
        ...ArticleCenteredArticle
        ...BlogThreeColumnCardsWithFilterArticle
        ...BlogThreeColumnCardsWithPaginationArticle
        ...BlogThreeColumnCardsArticle
        ...BlogThreeColumnWithBadgesArticle
        ...BlogTwoColumnWithSignUpArticle
      }
    }
    authors: allStrapiAuthor(filter: { id: { in: $authorIds } }) {
      nodes {
        id
      }
    }
    categories: allStrapiCategory(filter: { id: { in: $categoryIds } }) {
      nodes {
        ...BlogThreeColumnCardsWithFilterCategory
      }
    }
    products: allShopifyProcessedProduct(filter: { id: { in: $productIds } }) {
      nodes {
        ...ShopProductDetailWithImageGridProcessedProduct
        ...ShopProductListSimpleProductGridProcessedProduct
        ...ShopProductListWithInlineActionsAndExpandableSidebarFiltersProcessedProduct
      }
    }
    keywords: allStrapiKeyword(filter: { locale: { eq: $locale } }) {
      nodes {
        locale
        ...ArticleCenteredKeyword
      }
    }
  }
`;

const isObject = (o) => {
  return o && typeof o === "object" && !Array.isArray(o);
};

export default function DynamicTemplate({ data, pageContext, location }) {
  const pageData = data?.page || undefined;
  const layoutData = data?.layout || undefined;
  if (layoutData) {
    layoutData.componentsObject =
      layoutData.components?.reduce((obj, elem) => {
        return { ...obj, [elem.__typename]: elem };
      }, {}) || undefined;
  }
  const articles = data?.articles?.nodes || undefined;
  const authors = data?.authors?.nodes || undefined;
  const categories = data?.categories?.nodes || undefined;
  const products = data?.products?.nodes || undefined;
  const noHeroPages = data?.layout?.noHeroPages || undefined;
  const keywordTargetPairs = transformKeywords(
    data?.keywords?.nodes || undefined
  );

  const components = [];
  for (let componentData of pageData?.components || []) {
    // merge layout data with component data
    if (layoutData?.componentsObject?.[componentData.__typename]) {
      // componentData clean up
      Object.keys(componentData).forEach((key) => {
        if (
          !componentData[key] ||
          (Array.isArray(componentData[key]) && !componentData[key]?.length) ||
          (isObject(componentData[key]) &&
            !Object.keys(componentData[key]).length)
        ) {
          delete componentData[key];
        }
      });
      // merge layoutData and pageData component
      componentData = Object.assign(
        {},
        layoutData?.componentsObject?.[componentData.__typename],
        componentData
      );
    }
    // add shared components
    if (componentData.__typename === "StrapiComponentSharedSharedComponent") {
      for (let sharedComponentData of componentData.shared_component
        ?.components || []) {
        let Template = Templates[sharedComponentData.__typename];
        if (Template) {
          components.push({
            name: sharedComponentData.__typename,
            react: (
              <Suspense fallback={null}>
                <Template
                  location={location}
                  locale={pageContext.locale}
                  data={sharedComponentData}
                  blogData={pageContext.blogData}
                  articles={articles}
                  authors={authors}
                  categories={categories}
                  shopData={pageContext.shopData}
                  products={products}
                  keywordTargetPairs={keywordTargetPairs}
                />
              </Suspense>
            ),
          });
        } else {
          // Warning Template nicht gefunden.
          console.warn(
            `No template found for shared component "${sharedComponentData.__typename}".`
          );
        }
      }
    } else {
      // add page components
      let Template = Templates[componentData.__typename];
      if (Template) {
        components.push({
          name: componentData.__typename,
          react: (
            <Suspense fallback={null}>
              <Template
                location={location}
                locale={pageContext.locale}
                data={componentData}
                blogData={pageContext.blogData}
                articles={articles}
                authors={authors}
                categories={categories}
                shopData={pageContext.shopData}
                products={products}
                keywordTargetPairs={keywordTargetPairs}
              />
            </Suspense>
          ),
        });
      } else {
        // Warning Template nicht gefunden.
        console.warn(
          `No template found for component "${componentData.__typename}".`
        );
      }
    }
  }

  return (
    <>
      <NotificationToaster />
      <Layout
        layoutData={layoutData}
        locale={pageContext?.locale}
        languageLinks={pageContext?.languageLinks}
        switchBackground={!noHeroPages?.find(({ id }) => id === pageData?.id)}
        location={location}
      >
        {components.map((component, index) => (
          <div key={index}>{component.react}</div>
        ))}
      </Layout>
    </>
  );
}

export function Head({ pageContext }) {
  return (
    <>
      <html lang={pageContext.locale || "en"} />
      <Seo seoData={pageContext.seo} />
    </>
  );
}
